import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanInput, StanModal } from '@/components';
import { type AddAffiliateFormData, addAffiliateSchema } from '../../validations/affiliate';
import { useAffiliateActions } from '../../queries/useAffiliateActions';
import { stanNotify } from '@/lib';

interface AddAffiliateModalProps {
    isOpen: boolean;
    onClose: () => void;
    pageId: string;
}

export function AddAffiliateModal({ isOpen, onClose, pageId }: AddAffiliateModalProps) {
    const { addAffiliate } = useAffiliateActions(pageId);
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AddAffiliateFormData>({
        resolver: zodResolver(addAffiliateSchema),
        defaultValues: {
            full_name: '',
            email: '',
        },
    });

    const onSubmit = (data: AddAffiliateFormData) => {
        addAffiliate.mutate(
            { full_name: data.full_name, email: data.email },
            {
                onSuccess: () => {
                    handleClose();
                    stanNotify({
                        type: 'success',
                        title: 'Success',
                        text: 'Affiliate has been added',
                    });
                },
                onError: () => {
                    stanNotify({
                        type: 'error',
                        title: 'Add Affiliate',
                        text: "Couldn't add Affiliate. Please try again later.",
                    });
                },
            }
        );
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <StanModal
            id='add-affiliate-modal'
            isOpen={isOpen}
            title='Add an Affiliate'
            primaryActionLabel='Add Affiliate'
            secondaryActionLabel='Cancel'
            onPrimaryAction={handleSubmit(onSubmit)}
            onSecondaryAction={handleClose}
            onClose={handleClose}
            primaryActionLoading={addAffiliate.isPending}
            primaryActionDisabled={addAffiliate.isPending}
            size='sm'
        >
            <div className='text-gray mb-4 text-center font-semibold'>
                Grant affiliate access and send them their unique link.
            </div>
            <div className='flex flex-col gap-3'>
                <Controller
                    name='full_name'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='affiliate-full-name'
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder='Name'
                            error={!!errors.full_name}
                            hintText={errors.full_name?.message}
                        />
                    )}
                />
                <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='affiliate-email'
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder='Email'
                            error={!!errors.email}
                            hintText={errors.email?.message}
                        />
                    )}
                />
            </div>
        </StanModal>
    );
}
